import React from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Crown, Loader2, Tag, X, CreditCard } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API, useApp } from "@/state/AppContext";
import { toast } from "sonner";

function PaypalGlyph() {
  return (
    <svg width="12" height="14" viewBox="0 0 24 28" aria-hidden className="inline">
      <path d="M20.5 6.9c.3 1.9-.4 3.9-1.9 5.4-1.7 1.7-4 2.4-6.5 2.4h-1.4a1 1 0 0 0-1 .8L8.4 22H5.1a.5.5 0 0 1-.5-.6L7.1 5.5A.5.5 0 0 1 7.6 5h6.8c2.1 0 4.1.5 5.3 1.5.4.3.6.6.8.9zm-2 1.6c-.5-1-1.9-1.4-3.6-1.4H9.6L8.3 15.4h1.4c1.7 0 3.3-.4 4.4-1.4 1.2-1 1.8-2.5 1.5-4.2-.1-.6-.4-1-.7-1.3z" fill="#003087"/>
      <path d="M15 20.5c-1 1-2.6 1.4-4.3 1.4H9.3L8.9 24H5.6a.5.5 0 0 1-.5-.6l.4-2.7h4l1.3-8.3h1.7c2.5 0 4.8-.7 6.5-2.4.7-.8 1.3-1.7 1.6-2.7.2 2-.5 4-2 5.5-1.7 1.7-4 2.4-6.5 2.4l-.5 3.2c1.4 0 2.7-.4 3.4-1z" fill="#009cde"/>
    </svg>
  );
}

const PLANS = [
  {
    key: "free", name: "Kostenlos",
    monthly_price: "0 €", yearly_price: "0 €",
    features: ["2 Dokumente · 7-Tage-Testphase", "Analyse & Zusammenfassung", "Übersetzung", "Fristen & Dringlichkeit"],
    monthly_lookup: null, yearly_lookup: null,
  },
  {
    key: "plus", highlight: true, name: "Plus",
    monthly_price: "5,99 € / Monat", yearly_price: "59,99 € / Jahr",
    yearly_hint: "≈ 5,00 € / Monat · 2 Monate gratis",
    features: ["15 Dokumente / Monat", "Alle Sprachen", "Antwortassistent", "Erinnerungen", "Dokumenthistorie"],
    monthly_lookup: "briefassist_plus_monthly",
    yearly_lookup: "briefassist_plus_yearly",
  },
  {
    key: "premium", name: "Premium",
    monthly_price: "8,99 € / Monat", yearly_price: "94,99 € / Jahr",
    yearly_hint: "≈ 7,92 € / Monat",
    features: ["30 Dokumente / Monat", "Komplexe PDFs", "Erweiterte Analyse", "Dokumentarchiv", "Erweiterte Antwortfunktionen"],
    monthly_lookup: "briefassist_premium_monthly",
    yearly_lookup: "briefassist_premium_yearly",
  },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { token, user } = useApp();
  const [busy, setBusy] = React.useState(null);
  const [interval, setInterval] = React.useState("month");
  const [promoInput, setPromoInput] = React.useState("");
  const [promo, setPromo] = React.useState(null);
  const [promoError, setPromoError] = React.useState("");
  const [validating, setValidating] = React.useState(false);

  const validatePromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setValidating(true); setPromoError("");
    try {
      const r = await axios.post(`${API}/promo/validate`, { code });
      setPromo(r.data);
      toast.success(`${r.data.label} · ${r.data.detail}`);
    } catch (e) {
      setPromo(null);
      setPromoError(e?.response?.data?.detail || "Code ungültig");
    } finally { setValidating(false); }
  };
  const clearPromo = () => { setPromo(null); setPromoInput(""); setPromoError(""); };

  const choose = async (plan) => {
    const lookup = interval === "year" ? plan.yearly_lookup : plan.monthly_lookup;
    if (!lookup) return;
    if (!user) { toast.info("Bitte zuerst anmelden."); navigate("/auth"); return; }
    setBusy(plan.key);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookup,
        origin_url: window.location.origin,
        promo_code: promo?.code || undefined,
      }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zahlung konnte nicht gestartet werden.");
      setBusy(null);
    }
  };

  return (
    <div className="px-5 pt-4 pb-8" data-testid="pricing-page">
      <button onClick={() => navigate(-1)} data-testid="pricing-back" className="text-slate-600 flex items-center gap-1 text-sm mb-3">
        <ArrowLeft className="w-4 h-4" /> Zurück
      </button>
      <h1 className="text-2xl font-bold text-slate-900">Wähle deinen Plan</h1>
      <p className="text-slate-600 mt-1">Zahle mit Karte oder PayPal. Jederzeit kündbar.</p>

      {/* Interval toggle */}
      <div className="mt-5 inline-flex bg-blue-50 border border-blue-100 rounded-full p-1" data-testid="interval-toggle">
        <button
          onClick={() => setInterval("month")}
          data-testid="interval-month"
          className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${interval === "month" ? "bg-blue-600 text-white" : "text-blue-700"}`}
        >Monatlich</button>
        <button
          onClick={() => setInterval("year")}
          data-testid="interval-year"
          className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${interval === "year" ? "bg-blue-600 text-white" : "text-blue-700"}`}
        >Jährlich · sparen</button>
      </div>

      {/* Promo code */}
      <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-3" data-testid="promo-block">
        <div className="flex items-center gap-2 text-sm text-slate-700 mb-2">
          <Tag className="w-4 h-4 text-blue-600" /> Promo-Code
        </div>
        {!promo ? (
          <div className="flex gap-2">
            <Input
              data-testid="promo-input"
              value={promoInput}
              onChange={e => setPromoInput(e.target.value.toUpperCase())}
              placeholder="Code eingeben"
              className="h-11 rounded-xl bg-blue-50/60 border-blue-100 uppercase"
            />
            <button
              onClick={validatePromo}
              disabled={validating || !promoInput.trim()}
              data-testid="promo-apply-btn"
              className="px-4 h-11 rounded-xl bg-blue-600 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
            >
              {validating && <Loader2 className="w-4 h-4 animate-spin" />}
              Anwenden
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="promo-applied">
            <div className="text-sm">
              <span className="font-semibold text-emerald-800">{promo.code}</span>
              <span className="text-emerald-700"> · {promo.detail}</span>
            </div>
            <button onClick={clearPromo} data-testid="promo-clear-btn" className="p-1.5 rounded-full hover:bg-emerald-100">
              <X className="w-4 h-4 text-emerald-700" />
            </button>
          </div>
        )}
        {promoError && <div className="mt-2 text-xs text-red-600" data-testid="promo-error">{promoError}</div>}
      </div>

      <div className="mt-5 space-y-4">
        {PLANS.map(p => {
          const price = interval === "year" ? p.yearly_price : p.monthly_price;
          const hasLookup = interval === "year" ? p.yearly_lookup : p.monthly_lookup;
          return (
            <div key={p.key} className={`rounded-3xl p-5 border ${p.highlight ? "bg-blue-50 border-blue-200 ring-2 ring-blue-100" : "bg-white border-slate-200"}`} data-testid={`plan-${p.key}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-slate-900">{p.name}</h3>
                    {p.highlight && <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full font-semibold">Beliebt</span>}
                  </div>
                  <div className="text-blue-700 font-semibold mt-1" data-testid={`plan-${p.key}-price`}>{price}</div>
                  {interval === "year" && p.yearly_hint && (
                    <div className="text-xs text-emerald-600 mt-0.5 font-medium">{p.yearly_hint}</div>
                  )}
                </div>
                {p.highlight && <Crown className="w-6 h-6 text-blue-600" />}
              </div>
              <ul className="mt-4 space-y-2">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => choose(p)}
                disabled={!hasLookup || busy === p.key}
                data-testid={`plan-${p.key}-btn`}
                className={`mt-4 w-full min-h-[48px] rounded-2xl font-semibold flex items-center justify-center gap-2 ${p.highlight ? "bg-blue-600 text-white" : hasLookup ? "bg-blue-50 text-blue-700 border border-blue-100" : "bg-slate-100 text-slate-500"}`}
              >
                {busy === p.key && <Loader2 className="w-5 h-5 animate-spin" />}
                {p.key === "free" ? "Aktueller Plan" : busy === p.key ? "Weiterleiten …" : "Auswählen"}
              </button>
              {hasLookup && (
                <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-slate-500" data-testid={`plan-${p.key}-methods`}>
                  <CreditCard className="w-3.5 h-3.5" />
                  <span>Kreditkarte</span>
                  <span className="mx-1">·</span>
                  <PaypalGlyph />
                  <span>PayPal</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
